import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight } from "@phosphor-icons/react";

const tabs = [
  { id: "a", label: "01", name: "Executive Assurance", path: "/concept-a" },
  { id: "b", label: "02", name: "Systems That Hold", path: "/concept-b" },
  { id: "c", label: "03", name: "Human-Centered Authority", path: "/concept-c" },
  { id: "d", label: "04", name: "Synthesis", path: "/concept-d" },
];

/**
 * Sticky concept switcher rendered at the top of each concept page.
 * Tone/theme is set by the parent via `tone` prop so the bar feels
 * native to its concept while remaining visually consistent.
 */
export default function ConceptSwitcher({ tone = "light", active }) {
  const location = useLocation();
  const isActive = (path) => (active ? active === path : location.pathname === path);

  const palettes = {
    // Concept A — ivory & deep green
    a: {
      bar: "bg-[#1A362D] text-[#FFFFF0] border-b border-[#1A362D]",
      pillIdle: "text-[#FFFFF0]/60 hover:text-[#FFFFF0]",
      pillActive: "bg-[#FFFFF0] text-[#1A362D]",
      brand: "text-[#FFFFF0]",
      back: "text-[#FFFFF0]/70 hover:text-[#FFFFF0]",
    },
    // Concept B — slate / forest
    b: {
      bar: "bg-[#0F1E18] text-[#F8F9FA] border-b border-[#0F1E18]",
      pillIdle: "text-[#F8F9FA]/60 hover:text-[#F8F9FA]",
      pillActive: "bg-[#F8F9FA] text-[#0F1E18]",
      brand: "text-[#F8F9FA]",
      back: "text-[#F8F9FA]/70 hover:text-[#F8F9FA]",
    },
    // Concept C — warm brown
    c: {
      bar: "bg-[#1F1F1F] text-[#FDFBF7] border-b border-[#1F1F1F]",
      pillIdle: "text-[#FDFBF7]/60 hover:text-[#FDFBF7]",
      pillActive: "bg-[#FDFBF7] text-[#1F1F1F]",
      brand: "text-[#FDFBF7]",
      back: "text-[#FDFBF7]/70 hover:text-[#FDFBF7]",
    },
    light: {
      bar: "bg-[#1A1A1A] text-[#FDFBF7] border-b border-[#1A1A1A]",
      pillIdle: "text-[#FDFBF7]/60 hover:text-[#FDFBF7]",
      pillActive: "bg-[#FDFBF7] text-[#1A1A1A]",
      brand: "text-[#FDFBF7]",
      back: "text-[#FDFBF7]/70 hover:text-[#FDFBF7]",
    },
  };

  const p = palettes[tone] || palettes.light;

  return (
    <div
      data-testid="concept-switcher"
      className={`sticky top-0 z-50 ${p.bar}`}
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 h-11 flex items-center justify-between gap-4">
        <Link
          to="/"
          data-testid="switcher-back-index"
          className={`text-[11px] uppercase tracking-[0.18em] font-medium font-chivo ${p.back} transition-colors`}
        >
          ← Concept Index
        </Link>

        <div className="flex items-center gap-1.5">
          <span
            className={`hidden sm:inline text-[10px] uppercase tracking-[0.22em] font-chivo mr-2 ${p.pillIdle}`}
          >
            Compare
          </span>
          {tabs.map((t) => {
            const active = isActive(t.path);
            return (
              <Link
                key={t.id}
                to={t.path}
                data-testid={`switcher-tab-${t.id}`}
                className={`text-[11px] uppercase tracking-[0.15em] font-chivo font-medium px-3 py-1.5 rounded-full transition-all ${
                  active ? p.pillActive : p.pillIdle
                }`}
              >
                <span className="sm:hidden">{t.label}</span>
                <span className="hidden sm:inline">{t.label} · {t.name}</span>
              </Link>
            );
          })}
        </div>

        <Link
          to="/"
          data-testid="switcher-brand"
          className={`hidden md:flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] font-chivo font-medium ${p.brand}`}
        >
          Faith 1st <ArrowUpRight size={12} weight="bold" />
        </Link>
      </div>
    </div>
  );
}
